import React, { useState, useEffect } from 'react';
import { uploadImageAndUpdateMenuItem } from '../../api/menu-api';

const ItemModal = ({ isOpen, item, sections, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    id: item?.id || '',
    name: item?.name || '',
    description: item?.description || '',
    name_arabic: item?.name_arabic || '',
    description_arabic: item?.description_arabic || '',
    price: item?.price || '',
    section_id: item?.section_id || '',
    image_url: item?.image_url || '',
    ...item
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {setImageFile(null)}, [])
  useEffect(() => {
    if (item) {
      setFormData({
        id: item.id || '',
        name: item.name || '',
        description: item.description || '',
        name_arabic: item.name_arabic || '',
        description_arabic: item.description_arabic || '',
        price: item.price || '',
        section_id: item.section_id || '',
        image_url: item.image_url || '',
        ...item
      });
    }
  }, [item]);

  useEffect(() => {
    if (!imageFile) {
      setPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setPreview(objectUrl);
    setFormData(prevFormData => ({ ...prevFormData, image_url: objectUrl }));
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const savedItem = await onSave({
      ...formData,
      price: parseFloat(formData.price),
      section_id: formData.section_id || item?.section_id,
    });
    if (imageFile) 
      await uploadImageAndUpdateMenuItem(imageFile, savedItem);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('File size too large (max 5MB)');
        return;
      }
      setImageFile(file);
      setFormData({ ...formData, image_url: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-4">
      <div className="bg-white p-4 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gold-dark">
          {item?.id ? "Edit Menu Item" : "Add New Menu Item"}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-3">
          {/* Section Selection */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Section *</label>
            <select
              className="w-full p-2 border border-gray-300 rounded"
              value={formData.section_id}
              onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
              required
            >
              <option value="">Select Section</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </div>

          {/* Name Field */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Item Name *</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Description Field */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Description</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded"
              rows="2"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

            {/* Arabic Name Field */}
            <div className="flex flex-col gap-1">
            <label className="text-gray-700">Item Name (Arabic) *</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded"
              value={formData.name_arabic}
              onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
              required
            />
          </div>

          {/* Arabic Description Field */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Description (Arabic)</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded"
              rows="2"
              value={formData.description_arabic}
              onChange={(e) => setFormData({ ...formData, description_arabic: e.target.value })}
            />
          </div>

          {/* Price Field */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Price *</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full pl-7 p-2 border border-gray-300 rounded"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Image Upload Field */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700">Item Image</label>
            <div className="flex flex-col items-start gap-3">
              <label className="cursor-pointer bg-amber-100 text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-200 transition-colors text-sm">
                <span>{imageFile ? 'Change Image' : 'Upload Image'}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
              <div className="flex flex-col w-full items-center gap-1 mt-2">
                <div className="max-[350px]:h-24 max-[350px]:w-24 max-[400px]:h-28 max-[400px]:w-28 h-32 w-32 border flex justify-center items-center text-center">
                  {preview?
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-[350px]:h-24 max-[350px]:w-24 max-[400px]:h-28 max-[400px]:w-28 h-32 w-32 object-cover rounded-lg border"
                  />
                  : 'image will be shown here'}
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gold-dark text-white rounded hover:bg-gold"
            >
              {item?.id ? "Save Changes" : "Add Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemModal;